import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import {
  ArrowRight,
  BookOpenCheck,
  Clock,
  Code2,
  Handshake,
  KeyRound,
  Layers,
  MessagesSquare,
  PlugZap,
  Sparkles,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { AiMemberFormDialog } from './ai-member-form-dialog'
import { ByokStartDialog } from './byok-start-dialog'
import { byokProviders } from './byok-start-lib'
import { useAiCredits, useAiSources } from '@/hooks/use-ai-credits'
import { walletHeadline } from '@/lib/ai-credits'
import { AI_PATHS } from '@/lib/ai-khung'

/** Biểu tượng của từng điểm mạnh dựng sẵn — chữ (title/desc) tính trong component để đọc bản dịch mới nhất. */
const BUILT_IN_FEATURE_ICONS = [BookOpenCheck, Layers, Clock, Handshake, MessagesSquare, Sparkles]

/**
 * Hộp chọn cách có nhân viên AI — 3 cách (chốt 14/10/2026):
 *   (1) AI sẵn sàng làm việc — AI của eCDP → luồng tạo mới `/ai/nhan-vien/moi` (trừ lượt AI);
 *   (2) Tạo bằng Key của tôi — CÙNG luồng tạo nhân viên AI của eCDP, model chạy bằng key đã kết nối ở Nguồn AI (byok-start-dialog);
 *   (3) Kết nối bot bên ngoài — Webhook (bot tự dựng) và bot dựng sẵn ở nền tảng (Trợ lý OpenAI) gộp chung một form
 *       (ai-member-form-dialog.tsx); tạo xong mở màn nhân viên AI để giao kênh & giờ làm.
 */
export function AddAiMemberDialog({
  open,
  onOpenChange,
  canEdit = true,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Có quyền `ai.update` (backend kiểm tra khi tạo / sửa nhân viên AI). Không có → không hiện nút tạo / kết nối. */
  canEdit?: boolean
}) {
  const { t } = useTranslation('team')
  const navigate = useNavigate()
  const [externalOpen, setExternalOpen] = useState(false)
  const [byokOpen, setByokOpen] = useState(false)
  // Ví credit (AI hai chế độ) — API chưa có thì không hiện dòng credit.
  const { data: credits } = useAiCredits()
  const { data: sources } = useAiSources()
  const keyProviders = byokProviders(sources)

  /** Điểm mạnh của nhân viên AI dựng sẵn — tính trong component để luôn đọc đúng bản dịch hiện tại. */
  const builtInFeatures = [
    { icon: BUILT_IN_FEATURE_ICONS[0], title: t('addAiMemberDialog.features.knowledge.title'), desc: t('addAiMemberDialog.features.knowledge.desc') },
    { icon: BUILT_IN_FEATURE_ICONS[1], title: t('addAiMemberDialog.features.channels.title'), desc: t('addAiMemberDialog.features.channels.desc') },
    { icon: BUILT_IN_FEATURE_ICONS[2], title: t('addAiMemberDialog.features.hours.title'), desc: t('addAiMemberDialog.features.hours.desc') },
    { icon: BUILT_IN_FEATURE_ICONS[3], title: t('addAiMemberDialog.features.handoff.title'), desc: t('addAiMemberDialog.features.handoff.desc') },
    { icon: BUILT_IN_FEATURE_ICONS[4], title: t('addAiMemberDialog.features.trial.title'), desc: t('addAiMemberDialog.features.trial.desc') },
    { icon: BUILT_IN_FEATURE_ICONS[5], title: t('addAiMemberDialog.features.selfLearn.title'), desc: t('addAiMemberDialog.features.selfLearn.desc') },
  ]
  /** Bot bên ngoài kết nối được (backend external-agents/): Webhook tự dựng + bot dựng sẵn ở nền tảng. */
  const externalPlatforms = ['Webhook', t('aiMemberFormLib.platforms.openaiAssistant')]

  function pick(mode: 'builtin' | 'byok' | 'external') {
    onOpenChange(false)
    if (mode === 'builtin') navigate(AI_PATHS.newBot)
    else if (mode === 'byok') setByokOpen(true)
    else setExternalOpen(true)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{t('addAiMemberDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('addAiMemberDialog.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 lg:grid-cols-[1.5fr_1fr]">
            {/* (1) Dựng sẵn — lựa chọn đề xuất */}
            <div className="relative flex flex-col rounded-xl border-2 border-primary/60 bg-gradient-to-b from-primary/[0.04] to-transparent p-5">
              <span className="absolute right-0 top-0 rounded-bl-xl rounded-tr-lg bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                {t('addAiMemberDialog.suggested')}
              </span>
              <Badge variant="default" className="self-start">{t('addAiMemberDialog.mostPopular')}</Badge>
              <h3 className="mt-3 text-lg font-medium">{t('addAiMemberDialog.builtinTitle')}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('addAiMemberDialog.builtinDesc')}
              </p>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {builtInFeatures.map((f) => (
                  <div key={f.title} className="flex gap-3 rounded-lg border bg-card p-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <f.icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{f.title}</p>
                      <p className="text-xs text-muted-foreground">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              {credits && (
                <p className="mt-4 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-sm">
                  <Trans
                    t={t}
                    i18nKey="addAiMemberDialog.creditNote"
                    values={{ balance: walletHeadline(credits).main }}
                    components={{ b: <b />, bal: <b className="tabular-nums" /> }}
                  />
                </p>
              )}
              {canEdit && (
                <div className="mt-5 flex justify-end">
                  <Button size="lg" onClick={() => pick('builtin')}>
                    {t('addAiMemberDialog.createButton')} <ArrowRight />
                  </Button>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-4">
              {/* (2) Key của tôi — cùng luồng tạo nhân viên AI của eCDP, chỉ khác nguồn token */}
              <div className="flex flex-1 flex-col rounded-xl border p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-medium">{t('addAiMemberDialog.byokTitle')}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t('addAiMemberDialog.byokDesc')}
                    </p>
                  </div>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <KeyRound className="h-5 w-5" />
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {keyProviders.length
                    ? keyProviders.map((p) => <Badge key={p.provider} variant="secondary">{p.name}</Badge>)
                    : <span className="text-xs text-muted-foreground">{t('addAiMemberDialog.noKeyConnected')}</span>}
                </div>
                {canEdit && (
                  <div className="mt-auto flex justify-end pt-4">
                    <Button variant="outline" onClick={() => pick('byok')}>
                      {t('addAiMemberDialog.chooseThis')} <ArrowRight />
                    </Button>
                  </div>
                )}
              </div>

              {/* (3) Bot bên ngoài — Webhook + bot dựng sẵn ở nền tảng, một form chung */}
              <div className="flex flex-1 flex-col rounded-xl border p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-medium">{t('addAiMemberDialog.externalTitle')}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t('addAiMemberDialog.externalDesc')}
                    </p>
                  </div>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-success/10 text-success">
                    <Code2 className="h-5 w-5" />
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {externalPlatforms.map((p) => <Badge key={p} variant="secondary">{p}</Badge>)}
                </div>
                {canEdit && (
                  <div className="mt-auto flex justify-end pt-4">
                    <Button variant="outline" onClick={() => pick('external')}>
                      {t('addAiMemberDialog.chooseThis')} <ArrowRight />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AiMemberFormDialog
        open={externalOpen}
        mode="webhook"
        bot={null}
        canEdit={canEdit}
        onClose={() => setExternalOpen(false)}
      />
      <ByokStartDialog open={byokOpen} onOpenChange={setByokOpen} />
    </>
  )
}
